<script setup lang="ts">
import { Loader2, X } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { SelectionAction, SelectionActionEvent } from './types';

const props = withDefaults(defineProps<{
    selectedCount: number;
    actions: SelectionAction[];
    loading?: boolean;
    selectionLabel?: string;
    loadingLabel?: string;
    clearLabel?: string;
}>(), {
    loading: false,
    selectionLabel: '',
    loadingLabel: 'Updating…',
    clearLabel: 'Clear selection',
});

const emit = defineEmits<{
    (event: 'action', payload: SelectionActionEvent): void;
    (event: 'clear'): void;
}>();

const resolvedSelectionLabel = computed(() => props.selectionLabel || `${props.selectedCount} selected`);

function selectAction(action: SelectionAction) {
    if (action.disabled || action.options?.length) return;
    emit('action', { actionId: action.id });
}
</script>

<template>
    <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="translate-y-full opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="translate-y-full opacity-0"
    >
        <div
            v-if="selectedCount > 0"
            class="fixed bottom-4 left-1/2 z-50 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center overflow-x-auto rounded-lg bg-neutral-900 px-3 py-2 text-white shadow-2xl sm:bottom-6 sm:px-5 sm:py-3"
            role="toolbar"
            :aria-label="`${resolvedSelectionLabel} actions`"
        >
            <template v-if="loading">
                <Loader2 class="mr-2 size-4 shrink-0 animate-spin text-neutral-400" />
                <span class="whitespace-nowrap text-sm text-neutral-400">{{ loadingLabel }}</span>
            </template>

            <template v-else>
                <span class="whitespace-nowrap text-sm font-medium">{{ resolvedSelectionLabel }}</span>
                <div class="mx-3 h-5 w-px shrink-0 bg-neutral-700 sm:mx-4" />

                <div class="flex items-center gap-1">
                    <template v-for="action in actions" :key="action.id">
                        <div v-if="action.separatorBefore" class="mx-2 h-5 w-px shrink-0 bg-neutral-700" />

                        <DropdownMenu v-if="action.options">
                            <DropdownMenuTrigger as-child>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    class="h-8 whitespace-nowrap text-white hover:bg-neutral-800 hover:text-white"
                                    :disabled="action.disabled || action.options.length === 0"
                                >
                                    <component :is="action.icon" v-if="action.icon" class="mr-1.5 size-4" />
                                    {{ action.label }}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side="top" align="start" class="min-w-44">
                                <template v-if="action.menuLabel">
                                    <DropdownMenuLabel>{{ action.menuLabel }}</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                </template>
                                <DropdownMenuItem
                                    v-for="option in action.options"
                                    :key="option.id"
                                    :disabled="option.disabled"
                                    @select="emit('action', { actionId: action.id, option })"
                                >
                                    <component :is="option.icon" v-if="option.icon" class="size-4" />
                                    {{ option.label }}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <Button
                            v-else
                            type="button"
                            size="sm"
                            variant="ghost"
                            class="h-8 whitespace-nowrap hover:bg-neutral-800"
                            :class="action.tone === 'destructive' ? 'text-red-400 hover:bg-red-900/30 hover:text-red-300' : 'text-white hover:text-white'"
                            :disabled="action.disabled"
                            @click="selectAction(action)"
                        >
                            <component :is="action.icon" v-if="action.icon" class="mr-1.5 size-4" />
                            {{ action.label }}
                        </Button>
                    </template>
                </div>

                <div class="mx-3 h-5 w-px shrink-0 bg-neutral-700 sm:mx-4" />
                <Button
                    type="button"
                    size="icon-sm"
                    variant="ghost"
                    class="size-8 shrink-0 text-white hover:bg-neutral-800 hover:text-white"
                    :title="clearLabel"
                    :aria-label="clearLabel"
                    @click="emit('clear')"
                >
                    <X class="size-4" />
                </Button>
            </template>
        </div>
    </Transition>
</template>
